import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
} from '@mui/material';

const Terms = () => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: 6,
            background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
          }}
        >
          <Typography
            component="h1"
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Terms of Service
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            Last updated 9th January 2025
          </Typography>

          <Divider sx={{ mb: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
            Welcome to wizcord ("the Service"), a platform that allows users to embed and utilise discord servers on their site for communication and community building without the need for a discord login. By using our Service, you agree to comply with these Terms of Service ("Terms"). If you do not agree with these Terms, please do not use the Service. This website-hosted user interface (this "Interface") is made available by Venture 2 RDC Limited, a company organized and existing under the laws of the United Kingdom (the "Company").
          </Typography>

          <Box sx={{ '& > *': { mb: 3 } }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                1. Eligibility
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                1.1. Age Requirement: You must be at least 18 years old or the legal age of majority in your jurisdiction, whichever is greater, to use the Service.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                1.2. Entertainment Purposes Only: The Service is provided for entertainment purposes only.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                2. User-Generated Content
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mt: 2 }}>
                2.1 Responsibility for Content
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                You are solely responsible for any text, files, images, data, or other materials ("Content") that you submit, transmit, or display through the plugin. We do not endorse or accept responsibility for any user-generated content.
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mt: 2 }}>
                2.2 Acceptable Use
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1 }}>
                You agree not to submit content that is:
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2, pl: 2 }}>
                • Unlawful, defamatory, abusive, harassing, or obscene<br />
                • Promoting hate, violence, or discrimination<br />
                • Infringing on the intellectual property rights of others<br />
                • In violation of Discord's own Terms of Service or Community Guidelines
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mt: 2 }}>
                2.3 Monitoring and Moderation
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                We do not actively monitor user content but reserve the right (without obligation) to remove or restrict access to content at our discretion and to cooperate with law enforcement where required.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                3. No Liability for User Content
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1 }}>
                We are not liable for any user-generated content, including but not limited to:
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2, pl: 2 }}>
                • Content you find offensive or inappropriate<br />
                • Content that causes loss, damage, or harm<br />
                • Content that violates Discord's or any third party's rules or laws
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                You acknowledge that use of the plugin is at your own risk.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                4. Third-Party Services
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                This plugin connects with Discord's API. We are not responsible for the availability, security, or content of Discord or any other third-party platform. Your use of Discord remains subject to their own terms and policies.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                5. Intellectual Property
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                All original content on this website, including the plugin's code and interface, remains the property of Venture 2 RDC Limited and is protected by copyright and other intellectual property laws.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                You may not copy, distribute, reverse-engineer, or create derivative works without our express permission.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                6. Data and Privacy
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                We process personal data in accordance with the UK GDPR and our [Privacy Policy]. By using the plugin, you consent to such processing and warrant that all data you provide is accurate.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                7. Anti-Money Laundering (AML) and Anti-Terrorism Financing (ATF) Compliance
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                7.1. We take active measures to comply with applicable AML and ATF regulations. This includes, but is not limited to, user verification, transaction monitoring, and reporting of suspicious activities to relevant authorities.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                7.2. By using the Service, you agree to provide accurate and complete information during the account registration and verification processes. Failure to comply may result in account suspension or termination.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                8. Content
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                8.1. You agree that all content you access or create on the Service, including text, images, or other materials, must comply with our guidelines and applicable laws.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                8.2. We reserve the right to remove or modify content at our sole discretion for any reason, including but not limited to violations of these Terms.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                9. Proprietary Rights
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                9.1. All intellectual property rights in the Service and its content, including but not limited to trademarks, copyrights, and software, are owned by us or our licensors. Unauthorized use is strictly prohibited.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                9.2. You may not reproduce, distribute, or create derivative works based on any content from the Service without explicit written permission.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                10. Modification, Suspension, and Termination
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                10.1. We reserve the right to modify, suspend, or terminate the Service or your access to it at any time, with or without notice, for any reason, including but not limited to violation of these Terms or legal obligations.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                10.2. In the event of termination, your right to use the Service ceases immediately, and any remaining funds in your account will be subject to the withdrawal process outlined in Section 3.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                11. Indemnification
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                11.1. You agree to indemnify, defend, and hold harmless the owners, operators, and affiliates of the Service from any claims, liabilities, damages, losses, or expenses arising out of your use of the Service or violation of these Terms.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                12. Disclosures and Disclaimers
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                12.1. The Service is provided "as is" without warranties of any kind, either express or implied. We disclaim all warranties, including but not limited to merchantability, fitness for a particular purpose, and non-infringement.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                12.2. We do not guarantee the accuracy, completeness, or reliability of any information on the Service.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                13. Limitation of Liability
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                13.1. To the maximum extent permitted by law, we shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of the Service, even if we have been advised of the possibility of such damages.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                14. Dispute Resolution
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                14.1. Any disputes arising from these Terms or your use of the Service will be resolved through binding arbitration in accordance with the rules of a recognized arbitration body. You waive the right to participate in any class action against the Service.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                14.2. The arbitration will take place in a mutually agreed location or virtually, and the decision of the arbitrator will be final and binding.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                15. General Information
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                15.1. These Terms constitute the entire agreement between you and the Service regarding its use and supersede any prior agreements.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                15.2. If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will remain in full force and effect.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                15.3. Our failure to enforce any right or provision under these Terms will not constitute a waiver of such right or provision.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                16. Contact Us
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                For questions or concerns regarding these Terms, please contact us at support@wizcord.io.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                By using the Service, you acknowledge that you have read, understood, and agreed to these Terms of Service.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Terms; 